/**
 * Coup de pouce, Attendu 1245 « mobiliser les connaissances sur la symétrie
 * axiale, le demi-tour, la translation » (3e, Transformations). Seconde
 * passe 3e (2026-09-13).
 *
 * Les cartes déléguées gardent la fiche de leur gen source, par clé :
 *   axiale_*     → 1070 (5e, « le bon symétrique ») ;
 *   demitour_*   → 951 (5e, « la méthode » du trajet compté) ;
 *   translation_* → pas de fiche en 182 : UNE page « Tout glisse pareil »
 *     (chaque point glisse de la même flèche, même longueur, même sens, la
 *     figure ne tourne pas).
 * La carte neuve « quelle transformation ? » et le V/F fusionné : UNE page
 * « Miroir, demi-tour, glissade », les trois images côte à côte sur la
 * même figure (un drapeau), pour choisir sans que la réponse soit servie :
 * l'axe et le centre ne sont pas ceux des cartes, aucune coordonnée.
 *
 * Pas de sacrifice propre (les fiches sources ont les leurs) ; les deux
 * pages neuves n'ont ni nombre ni coordonnée.
 */
import cp1070 from './1070';
import cp951 from './951';
import { svg } from './1054';

const ORANGE = '#EA580C';
const BLEU = '#2563EB';
const ENCRE = '#1E293B';
const GRIS = '#64748B';
const BLEU_CLAIR = '#DBEAFE';
const ORANGE_CLAIR = '#FED7AA';

const ligne = (y, contenu, taille = 12, couleur = ENCRE) => `<text x="200" y="${y}" font-size="${taille}" text-anchor="middle" fill="${couleur}">${contenu}</text>`;
/** Un drapeau (mât + fanion) donné par ses sommets, transformé par f. */
const DRAPEAU = [{ x: 0, y: 0 }, { x: 0, y: -44 }, { x: 26, y: -34 }, { x: 0, y: -24 }];
const drapeau = (f, fill, stroke) => {
    const P = DRAPEAU.map(f);
    return `<line x1="${P[0].x}" y1="${P[0].y}" x2="${P[1].x}" y2="${P[1].y}" stroke="${stroke}" stroke-width="2"/>
  <polygon points="${P[1].x},${P[1].y} ${P[2].x},${P[2].y} ${P[3].x},${P[3].y}" fill="${fill}" stroke="${stroke}" stroke-width="1.4"/>`;
};
const DEFS = `<defs><marker id="pte5" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto"><path d="M 0 0 L 7 3.5 L 0 7 z" fill="${ORANGE}"/></marker></defs>`;

// ─── Page « Tout glisse pareil » (translation) ─────────────────────────────
const T = (p) => ({ x: p.x + 110, y: p.y + 30 });
const ORIG = (p) => ({ x: p.x + 90, y: p.y + 120 });
const FLECHES = DRAPEAU.map((p) => { const a = ORIG(p); const b = T(a); return `<line x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}" stroke="${ORANGE}" stroke-width="1.3" stroke-dasharray="4 3" marker-end="url(#pte5)"/>`; }).join('\n  ');

const SVG_TRANSLATION = svg(
    'Un drapeau bleu et son image orange par une translation. Quatre flèches orange en pointillés relient chaque point à son image : toutes les flèches ont la même longueur, la même direction, le même sens. Tout glisse pareil, la figure ne tourne pas et n’est pas retournée.',
    `${DEFS}
  ${FLECHES}
  ${drapeau(ORIG, BLEU_CLAIR, BLEU)}
  ${drapeau((p) => T(ORIG(p)), ORANGE_CLAIR, ORANGE)}
  <text x="70" y="140" font-size="12" font-weight="bold" fill="${BLEU}">la figure</text>
  <text x="250" y="60" font-size="12" font-weight="bold" fill="${ORANGE}">son image</text>
  ${ligne(196, `Chaque point glisse de la ${'<tspan font-weight="bold" fill="' + ORANGE + '">même flèche</tspan>'} : même longueur, même sens.`, 12)}
  ${ligne(218, 'La figure ne tourne pas, elle n’est pas retournée : elle glisse.', 12)}
  ${ligne(240, 'Je trace la flèche depuis un sommet, je recopie la même depuis les autres.', 12, GRIS)}`,
    256,
);

// ─── Page « Miroir, demi-tour, glissade » (quelle transformation ?) ───────
const base = (cx) => (p) => ({ x: cx + p.x, y: 100 + p.y });
const miroir = (cx, axe) => (p) => ({ x: 2 * axe - (cx + p.x), y: 100 + p.y });
const demiTour = (cx, ox, oy) => (p) => ({ x: 2 * ox - (cx + p.x), y: 2 * oy - (100 + p.y) });
const glisse = (cx) => (p) => ({ x: cx + p.x + 46, y: 100 + p.y + 10 });

const SVG_TROIS = svg(
    'Trois fois le même drapeau bleu avec son image orange. Symétrie axiale : l’image est retournée comme dans un miroir, de l’autre côté de l’axe. Demi-tour, symétrie centrale : l’image est à l’envers, tête en bas, de l’autre côté du centre. Translation : l’image a glissé, dans le même sens, sans tourner.',
    `<!-- 1. miroir -->
  <line x1="88" y1="40" x2="88" y2="128" stroke="${GRIS}" stroke-width="1.2" stroke-dasharray="5 3"/>
  ${drapeau(base(54), BLEU_CLAIR, BLEU)}
  ${drapeau(miroir(54, 88), ORANGE_CLAIR, ORANGE)}
  <text x="88" y="148" font-size="12" font-weight="bold" text-anchor="middle" fill="${ENCRE}">miroir</text>
  <text x="88" y="163" font-size="11" text-anchor="middle" fill="${GRIS}">symétrie axiale</text>
  <text x="88" y="178" font-size="10" text-anchor="middle" fill="${GRIS}">retournée</text>

  <!-- 2. demi-tour -->
  <circle cx="200" cy="84" r="3" fill="${GRIS}"/>
  ${drapeau(base(176), BLEU_CLAIR, BLEU)}
  ${drapeau(demiTour(176, 200, 84), ORANGE_CLAIR, ORANGE)}
  <text x="200" y="148" font-size="12" font-weight="bold" text-anchor="middle" fill="${ENCRE}">demi-tour</text>
  <text x="200" y="163" font-size="11" text-anchor="middle" fill="${GRIS}">symétrie centrale</text>
  <text x="200" y="178" font-size="10" text-anchor="middle" fill="${GRIS}">à l’envers</text>

  <!-- 3. glissade -->
  ${drapeau(base(288), BLEU_CLAIR, BLEU)}
  ${drapeau(glisse(288), ORANGE_CLAIR, ORANGE)}
  <text x="316" y="148" font-size="12" font-weight="bold" text-anchor="middle" fill="${ENCRE}">glissade</text>
  <text x="316" y="163" font-size="11" text-anchor="middle" fill="${GRIS}">translation</text>
  <text x="316" y="178" font-size="10" text-anchor="middle" fill="${GRIS}">déplacée, sans tourner</text>

  ${ligne(210, 'Je regarde l’image : retournée, à l’envers, ou seulement déplacée ?', 12)}`,
    226,
);

const AIDE_TRANSLATION = { pages: [{ titre: 'Tout glisse pareil', svg: SVG_TRANSLATION }] };
const AIDE_TROIS = { pages: [{ titre: 'Miroir, demi-tour, glissade', svg: SVG_TROIS }] };

const CP_1245 = {
    axiale_bon_qcm_unique: cp1070.bon_qcm_unique,
    axiale_bon_tableau_vf: cp1070.bon_tableau_vf,
    demitour_centre_qcm_unique: cp951.centre_qcm_unique,
    demitour_coord_texte_trou_saisie: cp951.coord_texte_trou_saisie,
    demitour_image_grille_qcm_unique: cp951.image_grille_qcm_unique,
    translation_construire_image: AIDE_TRANSLATION,
    translation_coord_image_texte_trou: AIDE_TRANSLATION,
    translation_conservation_vf: AIDE_TRANSLATION,
    transformation_qcm_unique: AIDE_TROIS,
    sens_tableau_vf: AIDE_TROIS,
};

export default CP_1245;
